import { Component, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UserProfileService } from '@services/user-profile.service';
import { UserTicketService, FilterOperator } from '@6grain/masofa-monolith-api-client/common';
import { AuthService } from '@services/auth.service';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ServerTranslatePipe } from '@app/utils/pipes/server-translate-pipe';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { BackButton } from '@components/ui/back-button/back-button';
import { InputComponent } from '@components/input/input.component';
import { environment } from '@root/src/environments/environment';
import { buildFilterQuery } from '@app/utils/_msf';
import { SortType } from '@6grain/masofa-monolith-api-client/crop-monitoring/model/sortType';
import { forkJoin } from 'rxjs';
import {CustomMessageService} from '@app/interceptors/custom-message';

@Component({
  selector: 'app-profile-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    CardModule,
    TableModule,
    TagModule,
    DialogModule,
    FormsModule,
    ToastModule,
    BackButton,
  ],
  providers: [   {
    provide: MessageService,
    useClass: CustomMessageService
  }, DateFormatLangPipe],
  templateUrl: './profile-page.component.html',
  styleUrl: './profile-page.component.scss'
})
export class ProfilePageComponent implements OnInit {
  private userProfileService = inject(UserProfileService);
  private userTicketService = inject(UserTicketService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private http = inject(HttpClient);
  public translate = inject(TranslateService);
  private messageService = inject(MessageService);
  private cdr = inject(ChangeDetectorRef);

  // User info
  userProfile: any = null;
  loading = true;

  // Support tickets
  activeTickets: any[] = [];
  ticketsLoading = false;

  // Change password modal
  showChangePasswordModal = false;
  changePasswordForm = {
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  };
  showOldPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  isChangingPassword = false;

  ngOnInit(): void {
    this.loadUserProfile();
    this.loadActiveTickets();
  }

  loadUserProfile(): void {
    this.loading = true;

    // Always load fresh data from server
    this.userProfileService.loadProfile(this.authService.getToken() || '').subscribe({
      next: (serverProfile) => {
        console.log('📋 Profile loaded from server:', serverProfile);
        this.userProfile = {
          ...serverProfile,
          roles: this.authService.getUserRoles()
        };
        console.log('📋 Final userProfile:', this.userProfile);
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('❌ Error loading profile:', err);
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  loadActiveTickets(): void {
    this.ticketsLoading = true;

    const profile = this.userProfileService.getProfile();
    if (!profile?.id) {
      this.ticketsLoading = false;
      return;
    }

    // Backend enum: New = 0, InProgress = 1, Rejected = 2, Complite = 3
    // Make two parallel requests: one for New (0) and one for InProgress (1)
    // Then combine results on frontend
    const filtersNew = [
      {
        filterField: 'CreateUserName',
        filterValue: profile.userName || profile.email || profile.id,
        filterOperator: FilterOperator.NUMBER_0 // Equals
      },
      {
        filterField: 'TicketStatus',
        filterValue: 0, // New
        filterOperator: FilterOperator.NUMBER_0 // Equals
      }
    ];

    const filtersInProgress = [
      {
        filterField: 'CreateUserName',
        filterValue: profile.userName || profile.email || profile.id,
        filterOperator: FilterOperator.NUMBER_0 // Equals
      },
      {
        filterField: 'TicketStatus',
        filterValue: 1, // InProgress
        filterOperator: FilterOperator.NUMBER_0 // Equals
      }
    ];

    const requestNew = this.userTicketService.commonUserTicketGetByQueryPost({
      take: 10,
      offset: 0,
      sortBy: 'CreateAt',
      sort: SortType.NUMBER_1,
      filters: filtersNew
    });

    const requestInProgress = this.userTicketService.commonUserTicketGetByQueryPost({
      take: 10,
      offset: 0,
      sortBy: 'CreateAt',
      sort: SortType.NUMBER_1,
      filters: filtersInProgress
    });

    // Execute both requests in parallel and combine results
    forkJoin([requestNew, requestInProgress]).subscribe({
      next: ([newTickets, inProgressTickets]) => {
        console.log('📋 DEBUG: New tickets (status=0):', newTickets);
        console.log('📋 DEBUG: InProgress tickets (status=1):', inProgressTickets);

        // Combine and sort by CreateAt (newest first)
        const combined = [...(newTickets || []), ...(inProgressTickets || [])];
        console.log('📋 DEBUG: Combined length:', combined.length);

        this.activeTickets = combined
          .sort((a: any, b: any) => {
            const dateA = new Date(a.createAt).getTime();
            const dateB = new Date(b.createAt).getTime();
            return dateB - dateA; // DESC
          })
          .slice(0, 10); // Take top 10

        console.log('📋 DEBUG: this.activeTickets assigned:', this.activeTickets);
        console.log('📋 DEBUG: this.activeTickets.length:', this.activeTickets.length);
        console.log('📋 DEBUG: Setting ticketsLoading to false');

        this.ticketsLoading = false;
        this.cdr.detectChanges();

        console.log('📋 DEBUG: After detectChanges, ticketsLoading:', this.ticketsLoading);
      },
      error: (err) => {
        console.error('❌ DEBUG: Error loading tickets:', err);
        this.ticketsLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  getStatusSeverity(status: number): string {
    // Соответствует UserTicketStatus enum: New=0, InProgress=1, Rejected=2, Complite=3
    switch (status) {
      case 0: return 'info';      // New
      case 1: return 'warning';   // InProgress
      case 2: return 'danger';    // Rejected
      case 3: return 'success';   // Complite
      default: return 'secondary';
    }
  }

  getStatusLabel(status: number): string {
    // Соответствует UserTicketStatus enum: New=0, InProgress=1, Rejected=2, Complite=3
    switch (status) {
      case 0: return this.translate.instant('New');
      case 1: return this.translate.instant('In Progress');
      case 2: return this.translate.instant('Rejected');
      case 3: return this.translate.instant('Complete');
      default: return this.translate.instant('Unknown');
    }
  }

  openChangePasswordModal(): void {
    this.changePasswordForm = {
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
    this.showOldPassword = false;
    this.showNewPassword = false;
    this.showConfirmPassword = false;
    this.showChangePasswordModal = true;
  }

  closeChangePasswordModal(): void {
    this.showChangePasswordModal = false;
    this.changePasswordForm = {
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
  }

  changePassword(): void {
    // Validation
    if (!this.changePasswordForm.oldPassword || !this.changePasswordForm.newPassword) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('Ошибка'),
        detail: this.translate.instant('Пожалуйста, заполните все поля')
      });
      return;
    }

    if (this.changePasswordForm.newPassword !== this.changePasswordForm.confirmPassword) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('Ошибка'),
        detail: this.translate.instant('PASSWORDS_DO_NOT_MATCH')
      });
      return;
    }

    if (this.changePasswordForm.newPassword.length < 6) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('Ошибка'),
        detail: this.translate.instant('PASSWORD_MIN_LENGTH')
      });
      return;
    }

    this.isChangingPassword = true;

    const token = this.authService.getToken();
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    const changePasswordData = {
      oldPassword: this.changePasswordForm.oldPassword,
      newPassword: this.changePasswordForm.newPassword
    };

    this.http.post(`${environment.api}/identity/Account/ChangePassword`, changePasswordData, { headers })
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('Успешно'),
            detail: this.translate.instant('Пароль успешно изменен')
          });
          this.isChangingPassword = false;
          this.closeChangePasswordModal();
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.isChangingPassword = false;
          const errorMessage = error?.error?.message || error?.message || 'Не удалось изменить пароль';
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('Ошибка'),
            detail: this.translate.instant(errorMessage)
          });
          this.cdr.detectChanges();
        }
      });
  }

  goToTicket(ticket: any): void {
    this.router.navigate(['/help/support-tickets/chat', ticket.id]);
  }

  goToAllTickets(): void {
    this.router.navigate(['/help/support-tickets']);
  }

  getRoleDisplayName(role: string): string {
    // Translate role names
    const roleTranslations: Record<string, string> = {
      'SystemAdmin': 'Системный администратор',
      'ModuleAdmin': 'Администратор модуля',
      'Manager': 'Менеджер',
      'Operator': 'Оператор',
      'User': 'Пользователь личного кабинета',
      'Foreman': 'Бригадир',
      'FieldWorker': 'Полевой работник',
      'Application': 'Приложение',
      'Subscriber': 'Подписчик',
      'Internal': 'Внутренний'
    };

    return this.translate.instant(roleTranslations[role] || role);
  }
}

